/*
 * What the console says about Hangar's webhook at a provider (EAS project, Bitrise app): the recorded registration
 * `{status, at, reason}` the API returns on `EasSetup.webhook` and `BitriseSetup.webhook` (SPEC §24), as a pill, a
 * sentence and the provider's reason. Pure, no React. A webhook that is not registered never blocks builds: the
 * reconciler follows them by polling, so the note says that instead of alarming.
 */
import type { WebhookRegistration } from '@hangar/shared';

/** `StatusPill` keys (see `badges.tsx`): passed = succeeded tone, warning = awaiting, error = failed, pending = queued. */
export type WebhookPill = 'passed' | 'warning' | 'error' | 'skipped';

export type WebhookState = WebhookRegistration['status'] | 'not_attempted';

export interface WebhookLook {
  state: WebhookState;
  pill: WebhookPill;
  label: string;
  /** One sentence for people: what this means for the builds. */
  note: string;
  /** The provider's or Hangar's reason (skipped, failed); never a token. */
  reason: string | null;
  at: string | null;
}

const POLLING = 'Builds are still followed by polling, with a delay.';

export function webhookLook(webhook: WebhookRegistration | null | undefined, provider: 'EAS' | 'Bitrise'): WebhookLook {
  if (!webhook) {
    return { state: 'not_attempted', pill: 'skipped', label: 'Webhook not registered yet', note: `Hangar has not tried to register its ${provider} webhook for this app. ${POLLING}`, reason: null, at: null };
  }
  const reason = webhook.reason?.trim() || null;
  switch (webhook.status) {
    case 'registered':
      return { state: 'registered', pill: 'passed', label: 'Webhook registered', note: `${provider} tells Hangar about build changes as they happen.`, reason: null, at: webhook.at };
    case 'skipped':
      return { state: 'skipped', pill: 'warning', label: 'Webhook skipped', note: `Hangar had nothing to register the ${provider} webhook with. ${POLLING}`, reason, at: webhook.at };
    case 'failed':
      return { state: 'failed', pill: 'error', label: 'Webhook failed', note: `${provider} refused the webhook registration. ${POLLING}`, reason, at: webhook.at };
  }
}

export interface WebhookAction {
  label: 'Register webhook' | 'Repair webhook';
}

/**
 * The button the EAS card offers under the webhook line: none once it is registered; "Register" when Hangar never tried,
 * "Repair" after a skip (the Hangar may have a hooks URL by now) or a refusal. Calling it again is always safe: it is idempotent.
 */
export function webhookActionOf(webhook: WebhookRegistration | null | undefined): WebhookAction | null {
  if (webhook?.status === 'registered') return null;
  return { label: webhook ? 'Repair webhook' : 'Register webhook' };
}
